import React, { useState, useEffect } from 'react';
import SafeIcon from '../common/SafeIcon';
import supabaseService from '../services/supabaseService';
import * as FiIcons from 'react-icons/fi';

const { FiX, FiDatabase, FiDownload, FiUpload, FiSync, FiCheck, FiAlertCircle, FiInfo, FiWifi, FiWifiOff, FiRefreshCw, FiActivity, FiShield } = FiIcons;

const DatabaseStorageModal = ({ isOpen, onClose, bookmarks, onBookmarksUpdate, bookmarksCount }) => {
  const [activeTab, setActiveTab] = useState('sync');
  const [status, setStatus] = useState(null);
  const [serverStats, setServerStats] = useState(null);
  const [isOnline, setIsOnline] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [storageInfo, setStorageInfo] = useState(null);

  useEffect(() => {
    if (isOpen) {
      checkServerStatus();
      fetchServerStats();
      setStorageInfo(supabaseService.getStorageInfo());
    }
  }, [isOpen]);

  const checkServerStatus = async () => {
    setIsChecking(true);
    const online = await supabaseService.checkConnection();
    setIsOnline(online);
    setIsChecking(false);
  };

  const fetchServerStats = async () => {
    try {
      const result = await supabaseService.getStatistics();
      if (result.success) {
        setServerStats(result.stats);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const handleOperation = async (operation, operationName) => {
    setStatus({ loading: true, message: `${operationName}...` });
    try {
      const result = await operation();
      if (result.success) {
        setStatus({
          success: true,
          message: result.message || `${operationName} completed successfully`
        });
        
        // Refresh stats and files after successful operation
        if (operationName.includes('Sync') || operationName.includes('Import')) {
          setTimeout(() => {
            fetchServerStats();
          }, 1000);
        }
        
        setTimeout(() => setStatus(null), 3000);
      } else {
        setStatus({
          error: true,
          message: result.error || `${operationName} failed`
        });
        setTimeout(() => setStatus(null), 5000);
      }
      
      return result;
    } catch (error) {
      setStatus({
        error: true,
        message: error.message || `${operationName} failed`
      });
      setTimeout(() => setStatus(null), 5000);
      return { success: false, error: error.message };
    }
  };

  const handleSyncToDatabase = () => {
    return handleOperation(
      () => supabaseService.saveBookmarks(bookmarks),
      'Syncing to database'
    );
  };

  const handleSyncFromDatabase = async () => {
    const result = await handleOperation(
      () => supabaseService.getAllBookmarks(),
      'Syncing from database'
    );
    
    if (result.success && onBookmarksUpdate) {
      onBookmarksUpdate(result.bookmarks);
    }
    
    return result;
  };

  const handleExportFromDatabase = () => {
    return handleOperation(
      () => supabaseService.exportBookmarks(),
      'Exporting from database'
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiDatabase} className="w-4 h-4 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900">Database Storage</h2>
            <div className="flex items-center gap-2">
              {isChecking ? (
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-blue-500 animate-spin" />
              ) : (
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${isOnline ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                  <SafeIcon icon={isOnline ? FiWifi : FiWifiOff} className="w-3 h-3" />
                  {isOnline ? 'Online' : 'Offline'}
                </div>
              )}
              <button 
                onClick={checkServerStatus} 
                className="p-1 hover:bg-slate-100 rounded" 
                title="Refresh connection status"
              >
                <SafeIcon icon={FiRefreshCw} className="w-4 h-4 text-slate-500" />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Connection Status Banner */}
        {!isOnline && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-3">
              <SafeIcon icon={FiWifiOff} className="w-5 h-5 text-red-600" />
              <div>
                <h3 className="font-medium text-red-800 text-sm">Database Connection Failed</h3>
                <p className="text-xs text-red-700">
                  Unable to connect to Supabase. Check your internet connection.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Status Display */}
        {status && (
          <div className={`mx-6 mt-4 p-4 rounded-lg border ${
            status.success ? 'bg-green-50 border-green-200 text-green-800' : 
            status.error ? 'bg-red-50 border-red-200 text-red-800' : 
            'bg-blue-50 border-blue-200 text-blue-800'
          }`}>
            <div className="flex items-center gap-2">
              {status.loading ? (
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
              ) : (
                <SafeIcon icon={status.success ? FiCheck : FiAlertCircle} className="w-4 h-4" />
              )}
              <span className="text-sm font-medium">{status.message}</span>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          <button 
            onClick={() => setActiveTab('sync')} 
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'sync' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <SafeIcon icon={FiSync} className="w-4 h-4 inline mr-2" />
            Sync
          </button>
          <button 
            onClick={() => setActiveTab('stats')} 
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'stats' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <SafeIcon icon={FiActivity} className="w-4 h-4 inline mr-2" />
            Statistics
          </button>
          <button 
            onClick={() => setActiveTab('info')} 
            className={`flex-1 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === 'info' ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <SafeIcon icon={FiInfo} className="w-4 h-4 inline mr-2" />
            Info
          </button>
        </div>

        <div className="p-6">
          {/* Sync Tab */}
          {activeTab === 'sync' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiSync} className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Database Synchronization
                </h3>
                <p className="text-slate-600 mb-6">
                  Sync your AI apps with Supabase. Your data is stored securely in a PostgreSQL database with row-level security.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Sync to Database */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiUpload} className="w-4 h-4 text-blue-600" />
                    </div>
                    <h4 className="font-medium text-slate-900">Upload to Database</h4>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Save your current {bookmarksCount} AI apps to Supabase.
                  </p>
                  <button 
                    onClick={handleSyncToDatabase} 
                    disabled={!isOnline || status?.loading}
                    className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sync to Database
                  </button>
                </div>

                {/* Sync from Database */}
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
                      <SafeIcon icon={FiDownload} className="w-4 h-4 text-green-600" />
                    </div>
                    <h4 className="font-medium text-slate-900">Download from Database</h4>
                  </div>
                  <p className="text-sm text-slate-600 mb-4">
                    Load AI apps from Supabase (will replace current data).
                  </p>
                  <button 
                    onClick={handleSyncFromDatabase} 
                    disabled={!isOnline || status?.loading}
                    className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Sync from Database
                  </button>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-blue-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-blue-900 mb-1">How Supabase Storage Works</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• Data is stored in a PostgreSQL database in Supabase</li>
                      <li>• Row-level security ensures data protection</li>
                      <li>• Cross-device synchronization through cloud storage</li>
                      <li>• Structured data storage with powerful query capabilities</li>
                      <li>• Realtime updates capability</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Statistics Tab */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiActivity} className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Database Statistics
                </h3>
                <p className="text-slate-600 mb-6">
                  Overview of data stored in the database and usage statistics.
                </p>
              </div>

              {serverStats ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <SafeIcon icon={FiDatabase} className="w-5 h-5 text-blue-600" />
                      <h4 className="font-medium text-slate-900">Total Apps</h4>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{serverStats.totalBookmarks}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <SafeIcon icon={FiStar} className="w-5 h-5 text-yellow-600" />
                      <h4 className="font-medium text-slate-900">Favorites</h4>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{serverStats.favoriteBookmarks}</p>
                  </div>
                  <div className="bg-slate-50 rounded-lg p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <SafeIcon icon={FiTag} className="w-5 h-5 text-green-600" />
                      <h4 className="font-medium text-slate-900">Tags</h4>
                    </div>
                    <p className="text-2xl font-bold text-slate-900">{serverStats.totalTags}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8">
                  <SafeIcon icon={FiRefreshCw} className="w-8 h-8 text-slate-400 mx-auto mb-3 animate-spin" />
                  <p className="text-slate-600">Loading database statistics...</p>
                </div>
              )}

              {serverStats?.mostUsedTags && serverStats.mostUsedTags.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-900 mb-3">Most Used Tags</h4>
                  <div className="space-y-2">
                    {serverStats.mostUsedTags.map(({ tag, count }) => (
                      <div key={tag} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <span className="font-medium text-slate-700">{tag}</span>
                        <span className="text-sm text-slate-500">{count} apps</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {serverStats?.lastModified && (
                <div className="text-center text-sm text-slate-500">
                  Last updated: {new Date(serverStats.lastModified).toLocaleString()}
                </div>
              )}
            </div>
          )}

          {/* Info Tab */}
          {activeTab === 'info' && storageInfo && (
            <div className="space-y-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiShield} className="w-8 h-8 text-indigo-600" />
                </div>
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  Storage Information
                </h3>
                <p className="text-slate-600 mb-6">
                  Details about your Supabase database configuration and capabilities.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h4 className="font-medium text-slate-900">Configuration</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Provider:</span>
                      <span className="text-sm font-medium text-slate-900">{storageInfo.provider}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Type:</span>
                      <span className="text-sm font-medium text-slate-900">{storageInfo.type}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Endpoint:</span>
                      <span className="text-sm font-medium text-slate-900 truncate">{storageInfo.endpoint}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Table:</span>
                      <span className="text-sm font-medium text-slate-900 truncate">{storageInfo.tableName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-sm text-slate-600">Authentication:</span>
                      <span className="text-sm font-medium text-green-600">Using anon key</span>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h4 className="font-medium text-slate-900">Features</h4>
                  <div className="space-y-2">
                    {storageInfo.features.map((feature, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-600" />
                        <span className="text-sm text-slate-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <SafeIcon icon={FiInfo} className="w-5 h-5 text-indigo-600 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-indigo-900 mb-1">About Supabase</h4>
                    <p className="text-sm text-indigo-800">
                      Supabase provides a PostgreSQL database with built-in security features like row-level security.
                      Your AI Apps Directory data is stored securely in the cloud with automatic backups and version control.
                      The database can be accessed from any device, ensuring your data is always in sync.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-slate-200 p-6">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">
              Supabase • PostgreSQL Database • Row-Level Security
            </div>
            <button 
              onClick={onClose} 
              className="px-4 py-2 bg-slate-100 text-slate-700 rounded-lg hover:bg-slate-200 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DatabaseStorageModal;